import { useState, useRef, useEffect, useCallback, FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useSearch } from "../../hooks/useSearch";
import { Box, Button, TextField, CircularProgress } from "@mui/material";
import { Product } from "../../pages/Home/Home";

function SearchBox({
  setSearchProducts,
  category,
}: {
  setSearchProducts: (products: number[] | null) => void;
  category: number | undefined;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const [inputValue, setInputValue] = useState(initialSearch);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchClient = useSearch();

  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length === 0) {
        return;
      }

      setIsSearching(true);
      try {
        const searchOptions: any = {
          limit: 100,
        };

        if (category) {
          searchOptions.filter = `Categories = ${category}`;
        }

        const searchResults = await searchClient
          .index("stock")
          .search(`${searchQuery}`, searchOptions);

        const results = searchResults.hits as Product[];
        setSearchProducts(results.map((result) => result.ID));
      } catch (error) {
        console.error("Search error:", error);
        setSearchProducts(null);
      } finally {
        setIsSearching(false);
      }
    },
    [category, searchClient, setSearchProducts]
  );

  // Re-run search from URL param on mount (e.g. when navigating back)
  useEffect(() => {
    if (initialSearch) {
      handleSearch(initialSearch);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (inputRef.current) {
      inputRef.current.blur();
    }

    // Update URL search param
    const nextParams = new URLSearchParams(searchParams);
    if (inputValue) {
      nextParams.set("search", inputValue);
    } else {
      nextParams.delete("search");
    }
    setSearchParams(nextParams, { replace: true });

    handleSearch(inputValue);
  };

  const handleReset = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setInputValue("");
    setSearchProducts(null);

    // Remove search param from URL
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("search");
    setSearchParams(nextParams, { replace: true });

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onReset={handleReset}
      sx={{ display: "flex", gap: 2, alignItems: "center" }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search..."
        type="search"
        value={inputValue}
        onChange={(event) => {
          const newValue = event.target.value;
          setInputValue(newValue);
        }}
        InputProps={{
          endAdornment: isSearching && <CircularProgress size={20} />,
        }}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={inputValue.length === 0}
      >
        Search
      </Button>
      {inputValue.length > 0 && !isSearching && (
        <Button type="reset" variant="outlined">
          Clear
        </Button>
      )}
    </Box>
  );
}

export default function Search({
  setSearchProducts,
  category,
}: {
  setSearchProducts: (products: number[] | null) => void;
  category: number | undefined;
}) {
  return (
    <SearchBox setSearchProducts={setSearchProducts} category={category} />
  );
}
